'use client';

import { MatchesPlayers } from '@/modules/league/new-components/types';
import { getMatchColor } from '@/utils/actions/leagues/gameDay/functions/getMatchColor';

interface ScoreSectionProps {
  player: MatchesPlayers;
  team1Wins: number;
  team2Wins: number;
  scoresArray: {
    team1Score: number;
    team2Score: number;
  }[];
  setScoresArray: React.Dispatch<
    React.SetStateAction<
      {
        team1Score: number;
        team2Score: number;
      }[]
    >
  >;
  isAuthorized: boolean | null;
  isUserAbleToEnterScore: boolean;
}

interface ScoreBoxProps {
  player: MatchesPlayers;
  indexOfPlayer: number;
  teamNumber: number;
  isMatchCanceled: boolean;
  team1Wins: number;
  team2Wins: number;
  scoresArray: {
    team1Score: number;
    team2Score: number;
  }[];
  setScoresArray: React.Dispatch<
    React.SetStateAction<
      {
        team1Score: number;
        team2Score: number;
      }[]
    >
  >;
  isAuthorized: boolean | null;
  isUserAbleToEnterScore: boolean;
}

const ScoreBox = ({
  player,
  indexOfPlayer,
  teamNumber,
  isMatchCanceled,
  team1Wins,
  team2Wins,
  scoresArray,
  setScoresArray,
  isAuthorized,
  isUserAbleToEnterScore,
}: ScoreBoxProps) => {
  const isMatchScoreVisible = isMatchCanceled === false;
  const scoreValueGroup1 = isMatchScoreVisible
    ? scoresArray[indexOfPlayer]?.team1Score === 0 && player?.DateFinish === null
      ? ''
      : scoresArray[indexOfPlayer]?.team1Score
    : '';

  const scoreValueGroup2 = isMatchScoreVisible
    ? scoresArray[indexOfPlayer]?.team2Score === 0 && player?.DateFinish === null
      ? ''
      : scoresArray[indexOfPlayer]?.team2Score
    : '';

  // Ensure that placeholder is always a string
  const placeholderGroup1 = isMatchScoreVisible ? String(scoreValueGroup1 || '0') : 'C';
  const placeholderGroup2 = isMatchScoreVisible ? String(scoreValueGroup2 || '0') : 'C';

  const groupOrder = teamNumber === 1 ? [1, 2] : [2, 1];
  return (
    <input
      className={`flex h-[40px] w-[48px] items-center justify-center rounded-md border text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${getMatchColor(
        player,
        indexOfPlayer + 1,
        groupOrder,
        team1Wins,
        team2Wins
      )} ${isMatchScoreVisible ? 'cursor-pointer' : 'cursor-text !border-gray-900 !bg-gray-300/70 font-bold !text-gray-900'}`}
      min={0}
      max={99}
      type='number'
      inputMode='numeric'
      pattern='^\d+$'
      {...{
        name: teamNumber === 1 ? player?.T1a_FirstName : player?.T2a_FirstName,
        id: teamNumber === 1 ? player?.T1a_UserID : player?.T2a_UserID,
        value: isMatchScoreVisible ? (teamNumber === 1 ? scoreValueGroup1 : scoreValueGroup2) : 'C',
        placeholder: teamNumber === 1 ? placeholderGroup1 : placeholderGroup2,
      }}
      onChange={(e) => {
        const value = e.target.value.slice(0, 2);
        const updatedScoresArray = scoresArray.map((score, index) =>
          index === indexOfPlayer
            ? { ...score, [`team${teamNumber}Score`]: parseInt(value) || 0 }
            : score
        );
        setScoresArray(updatedScoresArray);
      }}
      onFocus={(e) => {
        if (isMatchScoreVisible) e.target.placeholder = '';
      }}
      onBlur={(e) => {
        if (isMatchScoreVisible) e.target.placeholder = '0';
      }}
      disabled={
        (!isAuthorized && !isMatchScoreVisible) || (!isUserAbleToEnterScore && !isMatchScoreVisible)
      }
    />
  );
};

export const EnterScoreSection = ({
  player,
  team1Wins,
  team2Wins,
  scoresArray,
  setScoresArray,
  isAuthorized,
  isUserAbleToEnterScore,
}: ScoreSectionProps) => {
  return (
    <div className='my-4 flex flex-col items-center justify-center' key={player?.T1a_UserID}>
      <div className='flex flex-row items-center gap-2'>
        {[...Array(player?.MaxGames)].map((_, i) => (
          <ScoreBox
            key={player?.T1a_UserID}
            player={player}
            indexOfPlayer={i}
            teamNumber={1}
            isMatchCanceled={player?.DidCancelMatch || false}
            team1Wins={team1Wins}
            team2Wins={team2Wins}
            scoresArray={scoresArray}
            setScoresArray={setScoresArray}
            isAuthorized={isAuthorized}
            isUserAbleToEnterScore={isUserAbleToEnterScore}
          />
        ))}
      </div>
      <div className='flex w-full flex-row items-center justify-center'>
        <div className='hidden w-full border-t-[0.5px] border-gray-200 px-2 md:block' />
        <div className='my-4 px-4 text-xs uppercase text-gray-300'>vs</div>
        <div className='hidden w-full border-t-[0.5px] border-gray-200 px-2 md:block' />
      </div>
      <div className='flex flex-row items-center gap-2'>
        {[...Array(player?.MaxGames)].map((_, i) => (
          <ScoreBox
            key={player?.T2a_UserID}
            player={player}
            indexOfPlayer={i}
            teamNumber={2}
            isMatchCanceled={player?.DidCancelMatch || false}
            team1Wins={team1Wins}
            team2Wins={team2Wins}
            scoresArray={scoresArray}
            setScoresArray={setScoresArray}
            isAuthorized={isAuthorized}
            isUserAbleToEnterScore={isUserAbleToEnterScore}
          />
        ))}
      </div>
    </div>
  );
};
